import React from 'react'
import { useTranslation } from 'react-i18next'
import { accentAt, StatTile, StatTileSkeleton } from '../ui/surface'

const SpeedValue = ({ download, upload }) => (
  <span className="inline-flex items-baseline gap-1.5">
    <span className="text-accent-cyan">{download}</span>
    <span className="material-icons self-center text-sm text-accent-cyan/70">arrow_downward</span>
    <span className="text-muted-600">/</span>
    <span className="text-accent-pink">{upload}</span>
    <span className="material-icons self-center text-sm text-accent-pink/70">arrow_upward</span>
    <span className="font-sans text-xs font-medium text-muted-500">Mbps</span>
  </span>
)

/** `actions` maps a tile key (online, load, speed, traffic) to a click handler; tiles without one stay static. */
const StatsCards = ({ onlineCount, totalDevices, averageLoadPercent, avgSpeedtest, trends, currentTraffic, isLoading, actions = {} }) => {
  const { t } = useTranslation();
  
  // Функция для форматирования тренда
  const formatTrend = (trendData, type, currentValue) => {
    if (!trendData && type !== 'devices' && type !== 'traffic') return null;
    
    let label = '';
    let direction = 'stable';
    
    if (type === 'devices') {
      // Математика: текущее из Prometheus - предыдущее из PostgreSQL
      const prevValue = trendData?.previous_value || 0;
      const currentFromPrometheus = currentValue || 0;
      const difference = currentFromPrometheus - prevValue;
      
      if (difference > 0) {
        direction = 'up';
        label = t('dashboard.trendUp', { value: difference });
      } else if (difference < 0) {
        direction = 'down';
        label = t('dashboard.trendDown', { value: difference });
      } else {
        direction = 'stable';
        label = t('dashboard.trendStable');
      }
    } else if (type === 'utilization') {
      // Математика для утилизации: текущее из Prometheus - предыдущее из PostgreSQL
      const prevValue = trendData?.previous_value || 0;
      const currentFromPrometheus = currentValue || 0;
      const difference = currentFromPrometheus - prevValue;
      
      if (Math.abs(difference) < 0.1) {
        direction = 'stable';
        label = t('dashboard.trendStable');
      } else if (difference > 0) {
        direction = 'up';
        label = t('dashboard.trendUpPercent', { value: difference.toFixed(1) });
      } else {
        direction = 'down';
        label = t('dashboard.trendDownPercent', { value: Math.abs(difference).toFixed(1) });
      }
    } else if (type === 'traffic') {
      // Математика для трафика: текущее из Prometheus - предыдущее из PostgreSQL
      const prevValue = trendData?.previous_value || 0;
      const currentFromPrometheus = currentValue || 0;
      const difference = currentFromPrometheus - prevValue;
      
      if (Math.abs(difference) < 0.1) {
        direction = 'stable';
        label = t('dashboard.trendStable');
      } else if (difference > 0) {
        direction = 'up';
        label = t('dashboard.trendUpGB', { value: difference.toFixed(1) });
      } else {
        direction = 'down';
        label = t('dashboard.trendDownGB', { value: Math.abs(difference).toFixed(1) });
      }
    } else if (trendData) {
      const { change_percent, change_direction } = trendData;
      const absPercent = Math.abs(change_percent || 0);
      
      if (type === 'traffic_percent') {
        label = `${change_direction === 'up' ? '+' : '-'}${absPercent.toFixed(1)}% за день`;
        direction = change_direction === 'up' ? 'up' : 'down';
      }
    }
    
    return {
      direction: direction,
      label: label
    };
  };

  const speed = (() => {
    if (!avgSpeedtest) return { value: '0 Mbps', split: false }
    if (typeof avgSpeedtest === 'number') return { value: `${avgSpeedtest} Mbps`, split: false }
    const { download, upload } = avgSpeedtest
    if (download != null && upload != null) {
      return { value: <SpeedValue download={download} upload={upload} />, split: true }
    }
    return { value: `${download ?? upload ?? 0} Mbps`, split: false }
  })()

  const grid = 'grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'

  if (isLoading) {
    return (
      <div className={grid}>
        {Array.from({ length: 4 }).map((_, index) => (
          <StatTileSkeleton key={index} />
        ))}
      </div>
    )
  }

  const tiles = [
    {
      key: 'online',
      eyebrow: t('dashboard.onlineDevices'),
      value: onlineCount,
      sublabel: t('dashboard.totalConnected', { count: totalDevices }),
      meta: 'SLA',
      icon: 'cloud_done',
      trend: formatTrend(trends?.online_devices, 'devices', onlineCount),
    },
    {
      key: 'load',
      eyebrow: t('dashboard.averageLoad'),
      value: `${averageLoadPercent}%`,
      sublabel: t('dashboard.averageAllDevices'),
      meta: t('dashboard.normalLessThan50'),
      icon: 'speed',
      trend: formatTrend(trends?.utilization, 'utilization', averageLoadPercent),
    },
    {
      key: 'speed',
      eyebrow: t('dashboard.averageSpeedtest'),
      value: speed.value,
      sublabel: speed.split ? t('dashboard.averageNetworkMetrics') : t('dashboard.minSLA50Mbps'),
      meta: t('dashboard.yesterday'),
      icon: 'show_chart',
    },
    {
      key: 'traffic',
      eyebrow: t('dashboard.dailyTraffic'),
      value: `${(currentTraffic?.current_traffic_gb || 0).toFixed(1)} GB`,
      sublabel: t('dashboard.currentTrafficVolume'),
      meta: t('dashboard.totalAllDevices'),
      icon: 'network_check',
      trend: formatTrend(trends?.traffic, 'traffic', currentTraffic?.current_traffic_gb),
    },
  ]

  return (
    <div className={grid}>
      {tiles.map(({ key, ...tile }, index) => {
        const onClick = actions[key]
        const interactive = onClick ? { as: 'button', onClick, title: t(`dashboard.tileAction.${key}`) } : {}
        return <StatTile key={key} quietCap accentOnHover {...tile} {...accentAt(index)} {...interactive} />
      })}
    </div>
  )
}

export default StatsCards

